import React, { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal, Save } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import HelpTip from '../../../components/HelpTip';
import { PROGRAM_OPTIONS, PROGRAM_HELP } from '../../../lib/doleSources';

const ORG_OPTIONS = ['PESOMAP', 'Tourism Council', 'Chamber of Commerce', 'LGU Dev Council',
  'Coop Federation', 'Rotary Club', 'DOLE Regional TWG', 'Barangay Council'];
const SPRS_FIELDS = [['registered', 'Registered / yr'], ['referred', 'Referred / yr'],
  ['vacancies', 'Vacancies / yr'], ['jobFairs', 'Job fairs / yr']];

const blank = () => ({ programs: [], leadership_orgs: [], awards: [], award_points: 0, sprs_target: {}, lmi_on_time: {} });

/** Whole-year inputs: the scored items the platform can't see, plus the office's SPRS targets. */
export default function YearSettings({ settings, saving, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blank());
  const [flash, setFlash] = useState(false);
  const dirty = useRef(false);   // once the staffer edits, don't let an unrelated reload wipe it

  // Re-seed from the server only when the value genuinely changes AND we have no unsaved edits.
  const settingsKey = JSON.stringify(settings || {});
  useEffect(() => {
    if (dirty.current) return;
    setDraft({ ...blank(), ...(settings || {}) });
  }, [settingsKey]);

  const touch = () => { dirty.current = true; };
  const toggle = (field, value) => { touch(); setDraft((d) => {
    const list = d[field] || [];
    return { ...d, [field]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] };
  }); };
  const setTarget = (k, v) => { touch(); setDraft((d) => ({ ...d, sprs_target: { ...d.sprs_target, [k]: Math.max(0, parseInt(v, 10) || 0) } })); };

  const save = async () => {
    // Send only the keys this panel owns; the merge-mode save leaves lmi_on_time untouched.
    const payload = {
      programs: draft.programs || [],
      leadership_orgs: draft.leadership_orgs || [],
      awards: draft.awards || [],
      award_points: draft.award_points || 0,
      sprs_target: draft.sprs_target || {},
    };
    const ok = await onSave(payload);
    if (ok) { dirty.current = false; setFlash(true); setTimeout(() => setFlash(false), 1400); }
  };

  const chip = (field, value) => {
    const on = (draft[field] || []).includes(value);
    const help = field === 'programs' ? PROGRAM_HELP[value] : null;
    return (
      <span key={value} className="inline-flex items-center gap-1">
        <button type="button" onClick={() => toggle(field, value)}
          className={`text-xs font-medium px-2.5 py-1.5 rounded-full border ${on ? 'bg-slate-900 text-white border-transparent' : 'bg-white text-slate-600 border-slate-300'}`}>
          {value}
        </button>
        {help && <HelpTip id={help} />}
      </span>
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-900">
        <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Set once a year: programs, awards & leadership</span>
        <span className="text-xs text-slate-400">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-4">
          {/* Group A: platform can't see these; they DO earn points */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-bold text-slate-800">Programs, awards &amp; leadership</h4>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white">YOU ENTER · EARNS POINTS</span>
            </div>
            <p className="text-[11px] text-slate-600">The platform <b>can’t see these</b> — enter them so they count toward your score.</p>
            <div>
              <h5 className="text-xs font-semibold text-slate-700 mb-1">DOLE programs implemented <span className="font-normal text-slate-500">(more than 10 = 25 pts)</span></h5>
              <div className="flex flex-wrap gap-2 items-center">
                {PROGRAM_OPTIONS.map((p) => chip('programs', p))}
                <span className="text-xs text-slate-500 ml-1">{(draft.programs || []).length} selected</span>
              </div>
            </div>
            <div>
              <h5 className="text-xs font-semibold text-slate-700 mb-1">Leadership in organizations <span className="font-normal text-slate-500">(8 or more = 10 pts)</span></h5>
              <div className="flex flex-wrap gap-2 items-center">
                {ORG_OPTIONS.map((o) => chip('leadership_orgs', o))}
                <span className="text-xs text-slate-500 ml-1">{(draft.leadership_orgs || []).length} selected</span>
              </div>
            </div>
            <div>
              <h5 className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">Awards received <span className="font-normal text-slate-500">(judged 0–10 · no DOLE formula)</span> <HelpTip id="awards" /></h5>
              <div className="flex items-center gap-3">
                <input type="range" min="0" max="10" value={draft.award_points || 0}
                  onChange={(e) => { touch(); setDraft((d) => ({ ...d, award_points: parseInt(e.target.value, 10) })); }}
                  className="flex-1 max-w-xs" style={{ accentColor: '#1d4ed8' }} />
                <span className="tabular-nums text-lg font-bold">{draft.award_points || 0}</span><span className="text-slate-400 text-sm">/10</span>
              </div>
            </div>
          </div>

          {/* Group B: office-set SPRS targets */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-bold text-slate-800">Your annual SPRS targets</h4>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">YOU SET · DOLE GRADES % OF TARGET</span>
              <HelpTip id="sprs" />
            </div>
            <p className="text-[11px] text-slate-600">DOLE doesn’t fix a number for registrations, referrals, vacancies and job fairs — you commit a yearly target in your SPRS and DOLE grades your % of it. The monthly goals are these ÷ 12. <b>(Career guidance, placement rate and LMI are fixed by DOLE — not set here.)</b></p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SPRS_FIELDS.map(([k, label]) => (
                <label key={k} className="text-xs text-slate-600">
                  <span className="block mb-1 font-medium">{label}</span>
                  <Input type="number" min="0" value={draft.sprs_target?.[k] ?? ''} onChange={(e) => setTarget(k, e.target.value)} className="h-9 tabular-nums" />
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button size="sm" onClick={save} disabled={saving} className={flash ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-blue-700 hover:bg-blue-800'}>
              <Save size={14} className="mr-1" /> {flash ? 'Saved ✓' : (saving ? 'Saving…' : 'Save yearly settings')}
            </Button>
            <span className="text-[11px] text-slate-400">Set once a year — update when they change. (Monthly numbers, including LMI on-time, are entered per month above.)</span>
          </div>
        </div>
      )}
    </div>
  );
}
